import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useLogin } from '../../context/LoginContext';
import { useSettings } from '../../context/SettingsContext';
import useProfile from '../../hooks/useProfile';
import logger from '../../utils/logger';
import DevicesPage from './Devices/DevicesPage';
import AutoLogoutPage from './AutoLogoutPage';
import DeveloperInfoPage from './DeveloperInfoPage';
import SettingsToggle from './SettingsToggle';
import './SettingsModal.css';

const SettingsModal = ({ isOpen, onClose }) => {
  const { currentUser, logout } = useLogin();
  const { settings, updateSetting } = useSettings();
  const { uploadAvatar, isUploading } = useProfile();
  const navigate = useNavigate();
  const modalRef = useRef(null);
  const overlayRef = useRef(null);
  const fileInputRef = useRef(null);
  const scrollableContentRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const [currentPage, setCurrentPage] = useState('settings'); // Track current page

  const handleLogout = async () => {
    try {
      await logout();
      onClose(); // Close the modal after successful logout
      // Explicitly navigate to login page after logout
      navigate('/login');
    } catch (error) {
      logger.error('Logout failed:', error);
      // Still close the modal and navigate to login even if logout fails
      onClose();
      navigate('/login');
    }
  };

  const saveScrollPosition = () => {
    if (scrollableContentRef.current) {
      scrollPositionRef.current = scrollableContentRef.current.scrollTop;
    }
  };

  const goToDevicesPage = () => {
    saveScrollPosition();
    setCurrentPage('devices');
  };
  const goToAutoLogoutPage = () => {
    saveScrollPosition();
    setCurrentPage('auto-logout');
  };
  const goToDeveloperInfoPage = () => {
    saveScrollPosition();
    setCurrentPage('developer-info');
  };
  const goBackToSettings = () => setCurrentPage('settings');

  // Reset to main settings page when modal opens
  // CRITICAL: This fixes state persistence bug where modal could open on wrong page
  useEffect(() => {
    if (isOpen) {
      setCurrentPage('settings');
      scrollPositionRef.current = 0; // Reset scroll position too
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Lock body scroll
      document.body.style.overflow = 'hidden';

      // Focus the modal
      if (modalRef.current) {
        modalRef.current.focus();
      }
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      // Restore body scroll
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Effect to restore scroll position when returning to the main settings page
  useLayoutEffect(() => {
    if (currentPage === 'settings' && scrollableContentRef.current) {
      // Use useLayoutEffect to set scroll position before the browser paints
      scrollableContentRef.current.scrollTop = scrollPositionRef.current;
    }
  }, [currentPage]);

  // Handle backdrop click
  const handleOverlayClick = (event) => {
    if (event.target === overlayRef.current) {
      onClose();
    }
  };

  const handleAvatarClick = () => {
    // Trigger file input click
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      await uploadAvatar(file);
      logger.info('Avatar uploaded successfully');
      // Note: The avatar will update automatically via Matrix sync events
      // which will trigger a re-render through the MatrixContext
    } catch (error) {
      logger.error('Failed to upload avatar:', error);
      // TODO: Show error notification to user
    }

    // Reset the input so the same file can be selected again
    event.target.value = '';
  };

  if (!isOpen) return null;

  return (
    <div 
      className="settings-modal-overlay"
      ref={overlayRef}
      onClick={handleOverlayClick}
    >
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
        accept="image/*"
      />
      <div 
        className="settings-modal"
        ref={modalRef}
        role="dialog"
        aria-labelledby="settings-modal-title"
        aria-modal="true"
        tabIndex={-1}
      >
        <div className="settings-content-wrapper">
          {/* Left Profile Section */}
          <div className="settings-profile-section">
            <div 
              className="profile-image-container"
              style={{ cursor: 'pointer', position: 'relative' }}
              title="Click to change avatar"
            >
              <img 
                src={currentUser?.avatar || '/images/AppIcon60x60.png'} 
                alt="Profile" 
                className="profile-image"
                onClick={handleAvatarClick}
                style={{ opacity: isUploading ? 0.5 : 1 }}
              />
              <img
                src="/images/ChangeProfileImageBadge.png"
                alt="Change Avatar"
                className="change-avatar-badge"
                onClick={handleAvatarClick}
                style={{ opacity: isUploading ? 0.5 : 1 }}
              />
              {isUploading && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  textShadow: '0 0 4px rgba(0,0,0,0.8)',
                }}>
                  Uploading...
                </div>
              )}
            </div>
            <div className="profile-name">{currentUser?.name || 'Guest'}</div>
            
            <div className="profile-form">
              <div className="form-group">
                <label className="form-label">Display Name</label>
                <input 
                  type="text" 
                  className="form-input" 
                  readOnly
                  defaultValue={currentUser?.name || ''}
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Matrix ID</label>
                <input
                  type="email"
                  className="form-input"
                  readOnly
                  defaultValue={currentUser?.username || currentUser?.matrixUserId || ''}
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Phone Number</label>
                <input 
                  type="tel" 
                  className="form-input" 
                  readOnly
                  defaultValue={currentUser?.phone || ''}
                  placeholder="Not provided"
                />
              </div>
            </div>
            
            <div className="profile-logout">
              <button className="logout-button" onClick={handleLogout}>
                <ion-icon name="log-out-outline" />
                Logout
              </button>
            </div>
          </div>

          {/* Right Settings Panel */}
          <div className="settings-right-panel">
            <div className="settings-modal-header-centered">
              <h2 id="settings-modal-title">Settings</h2>
              <button 
                className="settings-modal-close"
                onClick={onClose}
                aria-label="Close settings"
              >
                <ion-icon name="close-outline" />
              </button>
            </div>
            <hr className="settings-header-divider" />

            <div
              className="settings-scrollable-content"
              ref={scrollableContentRef}
            >
              <div className={`settings-page ${currentPage === 'settings' ? 'active' : ''}`}>
                <div className="settings-sections">
                  <div className="settings-section">
                    <div className="settings-section-header">General Settings</div>
                    <div className="settings-section-content">
                      <div className="settings-line">Theme preferences and display options</div>
                      <div className="settings-line">Language and regional settings</div>
                    </div>
                  </div>

                  <div className="settings-section">
                    <div className="settings-section-header">Privacy</div>
                    <div className="settings-section-content">
                      <SettingsToggle
                        icon="fa-eye"
                        label="Send Read Receipts"
                        checked={settings.sendPublicReadReceipts}
                        onChange={(value) => updateSetting('sendPublicReadReceipts', value)}
                      />
                      <SettingsToggle
                        icon="fa-keyboard"
                        label="Send Typing Indicators"
                        checked={settings.sendTypingIndicators}
                        onChange={(value) => updateSetting('sendTypingIndicators', value)}
                      />
                      <SettingsToggle
                        icon="fa-user-secret"
                        label="Always Appear Offline"
                        checked={settings.alwaysAppearOffline}
                        onChange={(value) => updateSetting('alwaysAppearOffline', value)}
                      />
                    </div>
                  </div>

                  <div className="settings-section">
                    <div className="settings-section-header">Security</div>
                    <div className="settings-section-content">
                      <button
                        className="settings-line-button"
                        onClick={goToAutoLogoutPage}
                      >
                        <span>Automatic Logout Timer</span>
                        <ion-icon name="chevron-forward-outline" />
                      </button>
                      <button 
                        className="settings-line-button my-devices-button" 
                        onClick={goToDevicesPage}
                      >
                        <span>My Devices</span>
                        <ion-icon name="chevron-forward-outline" />
                      </button>
                    </div>
                  </div>
                  <div className="settings-section">
                    <div className="settings-section-header">Help</div>
                    <div className="settings-section-content">
                      <button
                        className="settings-line-button"
                        onClick={goToDeveloperInfoPage}
                      >
                        <span>Developer Info</span>
                        <ion-icon name="chevron-forward-outline" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className={`settings-page ${currentPage === 'devices' ? 'active' : ''}`}>
                <DevicesPage onBack={goBackToSettings} />
              </div>

              <div className={`settings-page ${currentPage === 'auto-logout' ? 'active' : ''}`}>
                <AutoLogoutPage onBack={goBackToSettings} />
              </div>

              <div className={`settings-page ${currentPage === 'developer-info' ? 'active' : ''}`}>
                <DeveloperInfoPage onBack={goBackToSettings} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

SettingsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SettingsModal;